import { AcadDirectory } from "@components";
import { TournamentDoc } from "@defs/Tournament.type";
import { OrgProps } from "@defs/Types";
import {
  collection,
  query,
  limit,
  where,
  CollectionReference,
} from "@firebase/firestore";
import { usePaginatedCollection } from "@hooks";
import { useUserContext } from "@lib/AuthContext";
import { Button, Fade, Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFirestore } from "reactfire";
import TournamentCard from "./TournamentCard";

interface TournamentDirectoryProps {
  orgs: OrgProps[];
  orgsLoading: boolean;
}

const TournamentDirectory = ({
  orgs,
  orgsLoading,
}: TournamentDirectoryProps) => {
  const db = useFirestore();
  const { isAdmin } = useUserContext();
  const TournamentsRef = collection(
    db,
    "tournaments"
  ) as CollectionReference<TournamentDoc>;
  const q1 = query(TournamentsRef, limit(20));
  const q2 = query(
    q1,
    where(
      "orgId",
      "in",
      orgs.map((org) => org.schoolShortcode).filter((org, i) => i < 10)
    )
  );

  const {
    data: snapshot,
    loading,
    next,
    prev,
    nextDisabled,
    prevDisabled,
  } = usePaginatedCollection({
    query: isAdmin ? q1 : q2,
    limit: 20,
    orderBy: {
      field: "createdAt",
      direction: "desc",
    },
  });

  const tournaments = snapshot?.docs ?? [];

  const { t } = useTranslation("events");

  return (
    <AcadDirectory
      hasData={!!tournaments.length}
      loading={loading || orgsLoading}
    >
      <Stack gap={4}>
        <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}>
          {tournaments.map((doc) => (
            <Fade in key={doc.id}>
              <Grid item xs={1} sm={1} md={1} lg={1}>
                <TournamentCard doc={doc} />
              </Grid>
            </Fade>
          ))}
        </Grid>
        <Stack direction="row" gap={2} justifyContent="center">
          <Button
            disabled={prevDisabled || loading}
            onClick={prev}
            variant="contained"
          >
            {t("previous", { ns: "common" })}
          </Button>
          <Button
            disabled={nextDisabled || loading}
            onClick={next}
            variant="contained"
          >
            {t("next", { ns: "common" })}
          </Button>
        </Stack>
      </Stack>
    </AcadDirectory>
  );
};

export default TournamentDirectory;
